import { useCallback, useEffect, useRef, useState } from 'react';

const getFilteredAudioDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioDevices = devices.filter(
      (d) =>
        (d.kind === 'audioinput' || d.kind === 'audiooutput') &&
        d.deviceId &&
        d.deviceId !== 'default'
    );
    const inputs = audioDevices.filter((d) => d.kind === 'audioinput');
    const outputs = audioDevices.filter((d) => d.kind === 'audiooutput');
    const ids = audioDevices
      .map((d) => d.deviceId)
      .sort()
      .join(',');

    return { hasExternal: inputs.length > 1 || outputs.length > 1, idsKey: ids };
  } catch {
    return { hasExternal: false, idsKey: '' };
  }
};

/**
 * Shows an orange-dot indicator when external audio devices (Bluetooth / wired / USB)
 * are available. The dot is dismissed when the settings popup opens and re-appears
 * only if the device list changes afterward (new device connected, or devices
 * disconnected and reconnected).
 */
export const useExternalDeviceIndicator = (isSettingsOpen: boolean) => {
  const [showIndicator, setShowIndicator] = useState(false);
  const acknowledgedIdsRef = useRef<string | null>(null);
  const isDirtyRef = useRef(true);
  const hasOpenedRef = useRef(false);

  const acknowledge = useCallback(async () => {
    const { idsKey } = await getFilteredAudioDevices();
    acknowledgedIdsRef.current = idsKey;
    isDirtyRef.current = false;
    setShowIndicator(false);
  }, []);

  const checkDevices = useCallback(async () => {
    const { hasExternal, idsKey } = await getFilteredAudioDevices();

    if (acknowledgedIdsRef.current !== null && idsKey !== acknowledgedIdsRef.current) {
      isDirtyRef.current = true;
    }

    setShowIndicator(hasExternal && isDirtyRef.current);
  }, []);

  useEffect(() => {
    if (isSettingsOpen) {
      hasOpenedRef.current = true;
      acknowledge();
    } else if (hasOpenedRef.current) {
      hasOpenedRef.current = false;
      acknowledge();
    }
  }, [isSettingsOpen, acknowledge]);

  useEffect(() => {
    checkDevices();
    navigator.mediaDevices?.addEventListener('devicechange', checkDevices);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', checkDevices);
    };
  }, [checkDevices]);

  return showIndicator;
};
